import { useState, useRef, useEffect } from "react";
import { Check, ChevronDown } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

// Popover de idioma accesible (EN | ES) — CSS puro para evitar cualquier
// desplazamiento de scroll al montar/desmontar.
export default function LanguagePopover() {
  const { lang, setLang } = useLanguage();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        btnRef.current?.focus({ preventScroll: true });
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const select = (l) => {
    setLang(l);
    setOpen(false);
    btnRef.current?.focus({ preventScroll: true });
  };

  const opts = [
    ["en", "English"],
    ["es", "Español"],
  ];
  const curCode = lang === "en" ? "EN" : "ES";

  return (
    <div ref={wrapRef} className="relative">
      <button
        ref={btnRef}
        data-testid="lang-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls="lang-popover"
        aria-label="Language"
        onClick={() => setOpen((v) => !v)}
        className="press flex h-10 items-center gap-0.5 bg-transparent px-2 font-body text-[13px] tracking-[0.08em] text-[#111] hover:text-black focus:outline-none focus-visible:ring-1 focus-visible:ring-[#111]/25"
      >
        <span className="font-medium">{curCode}</span>
        <ChevronDown
          className={`h-3 w-3 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          strokeWidth={1.75}
        />
      </button>

      <ul
        id="lang-popover"
        data-testid="lang-popover"
        role="listbox"
        aria-label="Select language"
        aria-hidden={!open}
        className={`absolute right-0 z-[80] mt-2 w-44 origin-top overflow-hidden rounded-xl border border-[#e8e8e8] bg-white p-1 shadow-[0_12px_34px_-14px_rgba(0,0,0,0.25)] transition-[opacity,transform] duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] ${
          open ? "pointer-events-auto translate-y-0 opacity-100" : "pointer-events-none -translate-y-1.5 opacity-0"
        }`}
      >
        {opts.map(([l, label]) => (
          <li key={l} role="option" aria-selected={lang === l}>
            <button
              data-testid={`lang-opt-${l}`}
              tabIndex={open ? 0 : -1}
              onClick={() => select(l)}
              className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 font-body text-sm transition-colors duration-150 ${
                lang === l ? "bg-[#f5f5f3] font-semibold text-[#111]" : "text-[#444] hover:bg-[#f7f7f5]"
              }`}
            >
              <span>{label}</span>
              {lang === l && <Check className="h-4 w-4 text-[#111]" />}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
